/**
 * Friends List Component
 * Displays and manages user's friends
 */

import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box,
  Avatar,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material'
import {
  People as PeopleIcon,
  PersonRemove as PersonRemoveIcon,
  Message as MessageIcon,
  Block as BlockIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material'
import { socialApi } from '@api/endpoints/social'
import type { FriendDto } from '@api/types/social'
import { UserFacingFriendStatus } from '@api/types/social'

const AVATAR_GRADIENTS = [
  'linear-gradient(135deg, #667eea, #764ba2)',
  'linear-gradient(135deg, #f093fb, #f5576c)',
  'linear-gradient(135deg, #4facfe, #00f2fe)',
  'linear-gradient(135deg, #43e97b, #38f9d7)',
  'linear-gradient(135deg, #fa709a, #fee140)',
  'linear-gradient(135deg, #a18cd1, #fbc2eb)',
  'linear-gradient(135deg, #fccb90, #d57eeb)',
]

function getAvatarGradient(name: string): string {
  let hash = 0
  for (let i = 0; i < (name || '').length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length]
}

interface FriendsListProps {
  filterText?: string
}

export function FriendsList({ filterText = '' }: FriendsListProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Fetch friends
  const { data: friendsData, isLoading, error } = useQuery({
    queryKey: ['friends'],
    queryFn: socialApi.getFriends,
  })

  // Filter to only show connected friends, then apply text filter
  const friends = friendsData?.filter(f => {
    if (f.userFacingStatus !== UserFacingFriendStatus.Connected) return false

    // Apply text filter if provided
    if (filterText) {
      const isRequester = f.requestedByCurrentUser
      const displayName = f.displayName || (isRequester ? f.receiverName : f.requesterName) || ''
      const username = f.username || ''
      const query = filterText.toLowerCase()

      return displayName.toLowerCase().includes(query) || username.toLowerCase().includes(query)
    }

    return true
  }) || []

  // Remove friend mutation
  const removeFriendMutation = useMutation({
    mutationFn: (friendUserId: string) => socialApi.removeFriend(friendUserId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['friends'] })
    },
  })

  // Block user mutation
  const blockMutation = useMutation({
    mutationFn: (userId: string) => socialApi.blockUser({ userId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['friends'] })
    },
  })

  const handleMessage = (e: React.MouseEvent, userId: string) => {
    e.stopPropagation()
    // TODO: Implement messaging
    console.log('Message user:', userId)
  }

  const handleRemoveFriend = (e: React.MouseEvent, friend: FriendDto) => {
    e.stopPropagation()
    const isRequester = friend.requestedByCurrentUser
    const userId = friend.userId || (isRequester ? friend.receiverUserId : friend.requesterUserId)
    const displayName = friend.displayName || (isRequester ? friend.receiverName : friend.requesterName) || 'this user'

    if (confirm(`Are you sure you want to remove ${displayName} from your friends?`)) {
      removeFriendMutation.mutate(userId)
    }
  }

  const handleBlock = (e: React.MouseEvent, friend: FriendDto) => {
    e.stopPropagation()
    const isRequester = friend.requestedByCurrentUser
    const userId = friend.userId || (isRequester ? friend.receiverUserId : friend.requesterUserId)
    const displayName = friend.displayName || (isRequester ? friend.receiverName : friend.requesterName) || 'this user'

    if (confirm(`Are you sure you want to block ${displayName}?`)) {
      blockMutation.mutate(userId)
    }
  }

  const handleProfileClick = (userId: string) => {
    navigate(`/profile/${userId}`)
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error">
        {error instanceof Error ? error.message : 'Failed to load friends'}
      </Alert>
    )
  }

  if (!friends || friends.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8, opacity: 0.5 }}>
        <PeopleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 1 }} />
        <Typography variant="body1" color="text.secondary">
          No Friends Yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Start connecting with fellow adventurers!
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {friends.map((friend) => {
        const isRequester = friend.requestedByCurrentUser
        const userId = friend.userId || (isRequester ? friend.receiverUserId : friend.requesterUserId)
        const displayName = friend.displayName || (isRequester ? friend.receiverName : friend.requesterName) || ''
        const adventureCount = friend.stats?.adventuresCompleted ?? 0
        const mutualFriends = friend.mutualFriends

        return (
          <Box
            key={friend.id}
            onClick={() => handleProfileClick(userId)}
            sx={{
              bgcolor: 'background.paper',
              borderRadius: '14px',
              p: '12px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              boxShadow: '0 2px 12px rgba(0,0,0,0.3)',
              cursor: 'pointer',
              transition: 'background 0.15s',
              '&:hover': {
                filter: 'brightness(1.1)',
              },
            }}
          >
            {/* Avatar with gradient */}
            <Box sx={{ position: 'relative', flexShrink: 0 }}>
              <Avatar
                src={friend.avatar}
                alt={displayName}
                sx={{
                  width: 48,
                  height: 48,
                  background: friend.avatar ? undefined : getAvatarGradient(displayName),
                  fontSize: 18,
                  fontWeight: 700,
                }}
              >
                {displayName?.[0]?.toUpperCase()}
              </Avatar>
            </Box>

            {/* Info */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'text.primary',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {displayName}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25, flexWrap: 'wrap' }}>
                {adventureCount > 0 && (
                  <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                    {adventureCount} adventure{adventureCount !== 1 ? 's' : ''}
                  </Typography>
                )}
                {mutualFriends !== undefined && mutualFriends > 0 && (
                  <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                    · {mutualFriends} mutual
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
              <Tooltip title="Send Message">
                <IconButton
                  size="small"
                  onClick={(e) => handleMessage(e, userId)}
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '10px',
                    bgcolor: 'rgba(66,153,225,0.1)',
                    color: 'info.main',
                    '&:hover': { bgcolor: 'rgba(66,153,225,0.2)' },
                  }}
                >
                  <MessageIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Remove Friend">
                <IconButton
                  size="small"
                  onClick={(e) => handleRemoveFriend(e, friend)}
                  disabled={removeFriendMutation.isPending}
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '10px',
                    bgcolor: 'rgba(255,255,255,0.04)',
                    color: 'text.secondary',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                  }}
                >
                  <MoreVertIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}
